import { useState, type ReactNode } from "react";
import { tidyMoneyDecimals } from "../../model/moneyText";
import {
  ArrowRight,
  Copy,
  Eye,
  ExternalLink,
  Lock,
  Mail,
  HandHeart,
  MapPin,
  MessageCircle,
  MessageSquareQuote,
  PhoneCall,
  Send,
  Sparkles,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { useNavigate } from "react-router-dom";

import { useBrand } from "@/shared/brand";
import { Button } from "@/shared/ui/button";
import { cn } from "@/shared/lib/utils";

import { isExternalToolEnabled } from "../../model/externalToolModules";

import {
  citableFacts,
  type AcqFeedbackCode,
  type AcqOpportunityCard as Card,
  type AcqSuggestedAction,
} from "../../api/acqService";
import {
  actionLabelFor,
  actionSpecFor,
  canEnrichCard,
  companyProfileText,
  contactProvenanceText,
  FEEDBACK_LABEL,
  FIRST_PARTY_SOURCE,
  discoveredText,
  freshnessText,
  happenedDaysAgo,
  intentTypeLabel,
  isDataQualityFailure,
  monthDayText,
  sourceLabel,
  stateLabel,
  unlockPriceText,
} from "../../model/acqLabels";
import { copyText, DeliverablePanel } from "./DeliverablePanel";
import { ContactOthers } from "./ContactOthers";
import { FeedbackBar } from "./FeedbackBar";
import { ProductChips } from "./ProductChips";

/**
 * 「还剩 N 天」是这个产品的紧急度本身,不是装饰:投标截止 / 证书到期一过,这单就不是线索了。
 * 负数 = 已过期(今日商机页已排掉,线索库里能翻到);≤3 天按契约 §10 反而降分——太晚了,提示要变颜色。
 */
export function deadlineText(daysLeft: number): string {
  if (daysLeft < 0) return "已截止";
  if (daysLeft === 0) return "今天截止";
  return `还剩 ${daysLeft} 天`;
}

const ACTION_ICON: Record<AcqSuggestedAction, LucideIcon> = {
  CALL: PhoneCall,
  EMAIL: Mail,
  WECHAT: MessageCircle,
  VISIT: MapPin,
  WATCH: Eye,
  ANSWER: MessageSquareQuote,
  DM: Send,
};

/** 不认识的动作码也要有脸(见 `actionSpecFor` 的注释:动作取值域会长)。 */
function actionIconFor(action: AcqSuggestedAction | string): LucideIcon {
  return ACTION_ICON[action as AcqSuggestedAction] ?? ArrowRight;
}

export interface OpportunityCardProps {
  card: Card;
  /** 今日商机给序号(「今天该打的第 N 通电话」);线索库不给。 */
  rank?: number;
  /** 补全一条联系方式要扣多少积分。**null = 价钱没到货,面上写「按次计费」,不许编数。** */
  unlockPrice: number | null;
  /** 接手(免费)。主按钮在 `taken=false` 时就是它。 */
  onTake: (id: string) => Promise<void> | void;
  onUnlock: (id: string) => Promise<void> | void;
  onFeedback: (id: string, code: AcqFeedbackCode) => Promise<void> | void;
  onAction: (card: Card) => Promise<void> | void;
  busy?: boolean;
  /** 紧凑档(线索库):事实先只出第一条,其余点「还有 N 条事实」展开。 */
  compact?: boolean;
  /**
   * 分派控件(`AssignControl`)。**由页面递进来,卡自己不判角色** ——
   * 判角色要 `useAuth`,而这张卡在样式桩、在测试里都要能单独渲染;
   * 让它自己去读登录态,等于每加一处用它就多一个 Provider 依赖。
   */
  assignSlot?: ReactNode;
  /**
   * 点卡上的产品 chip 会发生什么(契约 §16.7 / §19.3:「点任一 chip 切到该产品页签」)。
   *
   * ★ **缺席 ⇒ chip 就是一块不能点的标签**(线索库抽屉、样式桩里都是这样)——
   *   一个长得像按钮却点不动的 chip 比一块标签坏。
   * ★ 它送出去的是那枚 chip 自己的 `code` 而不是名字:页签认的是码,
   *   名字在产品改名之后会对不上(而那时面上没有任何一处报错)。
   * ★ 0918 批次八:能点的是**每一枚**(主 + 次),不只主产品 —— 次产品那一枚点不动的话,
   *   「这家也能卖 B」这件事在面上就没有下一步。
   */
  onPickProduct?: (productCode: string) => void;
}

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * 商机卡 —— 契约 §4.1 的三条硬要求全在这里,改之前先读契约
 * ═══════════════════════════════════════════════════════════════════════════
 * ① `facts[]` 每条都要能**点开原文**:没有 evidenceId / sourceUrl 的整条不渲染
 *    (`citableFacts`)。点不开出处的"事实"就是编的引用。
 * ② `facts`(事实)与 `inference`(AI 推断)**分两块排版**,不是一段话。
 *    推断块带明写的「以下是 AI 推断，不是事实」—— 这句不是装饰,是这张卡能不能被信的前提。
 * ③ 主按钮**只有一个**,而且**第一下永远是免费的**(0917 v2,契约 §12.8):
 *    没接手 → 「接手(免费)」→ 拿到产物;接手过 → 才是 `ACTION_SPEC` 的那个动作按钮。
 *    收钱的事只剩一个**次按钮**「补全联系方式 · N 积分」,而且只在真的可能找得到时才露。
 *    ★ 上一版是反过来的:主按钮先要 50 积分才给电话。那个形状教给用户的是"点按钮=花钱",
 *    于是他对**所有**按钮都不敢点(新视线 108 次搜索 216 积分 0 线索然后停号)。
 * ═══════════════════════════════════════════════════════════════════════════
 */
export function OpportunityCard({
  card,
  rank,
  unlockPrice,
  onTake,
  onUnlock,
  onFeedback,
  onAction,
  busy,
  compact,
  assignSlot,
  onPickProduct,
}: OpportunityCardProps) {
  const navigate = useNavigate();
  const [showAllFacts, setShowAllFacts] = useState(!compact);
  const facts = citableFacts(card);
  const shownFacts = showAllFacts ? facts : facts.slice(0, 1);
  const brand = useBrand();
  const specAction = card.inference ? actionSpecFor(card.inference.suggestedAction) : null;
  /**
   * OEM 贴牌(§2.4):动作的落点外部工具(外呼/邮件系统)被品牌开关关掉时,不再把人送去
   * `/ai-huoke/tools?tool=…`(那里已经没有这张卡)。主按钮退成「复制话术 + 推进状态」——
   * 不整个藏掉:藏了用户就没法把这条推进到「已触达」。直营开关全开,这条永远不走。
   */
  const toolOff = !!specAction?.tool && !isExternalToolEnabled(specAction.tool, brand.modules);
  const action =
    specAction && toolOff ? { ...specAction, tool: undefined, copyOpener: true } : specAction;
  const ActionIcon = card.inference ? actionIconFor(card.inference.suggestedAction) : null;
  /** 自家客服会话那条线:原文在自家页面上,所以「看原文」「主按钮」都换一种说法。 */
  const firstParty = card.source === FIRST_PARTY_SOURCE;
  /**
   * 跨源合卡之后一家公司只有一张卡(契约 §14.2)。
   * ★ 只有一个源时**照旧显示那个源的名字**,不写「1 个来源」—— 那是一句没有信息的话。
   */
  const sources = card.sources ?? [];
  const multiSource = sources.length > 1;
  /**
   * 「这家最近」时间线。**两条起才摆**:只有一条时它与抬头那行的来源 + 时间逐字重复,
   * 一张卡上把同一件事说两遍,读的人会去找"这两条有什么不一样"。
   */
  const recent = (card.recentSignals ?? []).filter((s) => !!s?.source);
  const showRecent = recent.length > 1;
  /**
   * 联系方式那行小字挂在**够得着的方式**上(电话 / 邮箱 / 官网),不挂地址:
   * 只有地址的卡摆一句「来源未知」是白占一行 —— 那张卡本来就还没查过联系方式。
   */
  const hasReachable = !!(card.contact?.phone || card.contact?.email || card.contact?.website);

  // 价签口径与地图获客一致:有价写数,没价写「按次计费」,**永不编数**。
  // 1015 企业库:接了企业库时价是一个区间(公网命中 50 / 企业库命中 120),查到才扣。
  const priceText = unlockPriceText(unlockPrice, card.unlockCreditsMax);
  const companyText = companyProfileText(card.company);

  /**
   * 「补全联系方式」的三个条件缺一不可:
   *   · 没补过(`!unlocked`)——补过了再点是白花钱的入口;
   *   · 手上确实没号码(`!contact.phone`)——公告里自带联系人的那种卡不该再收一笔;
   *   · 主体是**公司**(`subjectType === "company"`)——知乎提问者、抖音账号本来就
   *     没有"官方电话"这种东西,对他们露这个按钮等于卖一个必然失败的动作。
   */
  const canEnrich = canEnrichCard(card);

  const runAction = async () => {
    if (action?.copyDeliverable && card.deliverable?.text) {
      await copyText(card.deliverable.text, "话术");
    } else if (action?.copyOpener && card.inference?.opener) {
      await copyText(card.inference.opener, "开场白");
    }
    await onAction(card);
    if (action?.openTarget && card.deliverable?.targetUrl) {
      window.open(card.deliverable.targetUrl, "_blank", "noopener,noreferrer");
    }
    if (action?.tool) navigate(`/ai-huoke/tools?tool=${action.tool}`);
  };

  return (
    <article className="overflow-hidden rounded-xl border bg-card shadow-sm">
      {/* ── 抬头 ───────────────────────────────────────────────────────── */}
      <header className="flex flex-wrap items-start gap-x-3 gap-y-2 border-b bg-muted/30 px-4 py-3">
        {/* ★ 序号与公司名在**同一个** flex 项里:拆成两项的话窄屏会把序号甩到单独一行。
            basis-full sm:basis-0 = 窄屏标题整块独占一行,不跟右侧分数抢宽度 ——
            抢了就会一个字一行竖着排(本仓 0916 的老账)。 */}
        <div className="flex min-w-0 flex-1 basis-full items-start gap-2.5 sm:basis-0">
          {rank != null && (
            <span className="mt-0.5 grid h-7 w-7 shrink-0 place-items-center rounded-full bg-primary text-sm font-bold text-primary-foreground">
              {rank}
            </span>
          )}
          <div className="min-w-0">
            {/* ★ line-clamp-2 不用 truncate:公司名被截一半等于没给信息,宁可占两行。 */}
            <h3 className="line-clamp-2 break-words text-base font-bold leading-tight">
              {card.subjectName}
            </h3>
            <p className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-2xs text-muted-foreground">
              {/* ★ 多源时这一格变成「N 个来源」,具体哪几个在下面的「这家最近」里逐条摆 ——
                  抬头写成「政府采购公告」会让人以为只有招标这一条,而合卡的价值恰恰是
                  "三条线同时有动作"。title 里给全集,鼠标一停就看得到。 */}
              <span title={multiSource ? sources.map(sourceLabel).join(" · ") : undefined}>
                {multiSource ? `${sources.length} 个来源` : sourceLabel(card.source)}
              </span>
              {card.region && <span>· {card.region}</span>}
              {happenedDaysAgo(card) != null && (
                <span>· {freshnessText(happenedDaysAgo(card))}</span>
              )}
              {discoveredText(card.detectedAt) && (
                <span title={card.detectedAt ?? undefined}>
                  · {discoveredText(card.detectedAt)}
                </span>
              )}
              {card.daysLeft != null && (
                <span
                  className={
                    card.daysLeft < 0
                      ? "text-muted-foreground line-through"
                      : card.daysLeft <= 3
                        ? "font-semibold text-destructive"
                        : "font-semibold text-warning"
                  }
                  title={card.deadlineAt ? `截止 ${card.deadlineAt}` : undefined}
                >
                  · {deadlineText(card.daysLeft)}
                </span>
              )}
              {/* ★ 意向 pill:「他想干什么」。与状态(走到哪一步)、动作(该做什么)是三件事,
                  而它是唯一一个决定**话术重心**的 —— 同样一条线,问价的人要听到数字。 */}
              {card.intentType && (
                <span className="rounded-full bg-foreground/10 px-1.5 py-px font-semibold text-foreground/80">
                  {intentTypeLabel(card.intentType)}
                </span>
              )}
              {/* ★ 状态徽后面带一句「为什么」:wrong_person / bad_phone 也落 LOST,
                只看状态会把**采集端的毛病**读成销售谈输了(契约 §4.2 修订)。
                ★ 0917 批次三:`stateReason` 挂在 title 上 —— 卡上位置有限,但
                「它为什么在这个状态」是线索库里最常被问的那句(线索库表格同一份口径)。
                缺席就**不给 title**:一个空 tooltip 比没有 tooltip 更像坏了。 */}
              <span
                className="rounded-full border px-1.5 py-px"
                title={card.stateReason || undefined}
              >
                {stateLabel(card.state)}
                {isDataQualityFailure(card.feedbackCode) && (
                  <span className="text-warning"> · {FEEDBACK_LABEL[card.feedbackCode!]}</span>
                )}
              </span>
              {/* ★★ 0918 批次八(契约 §19.3):产品 chip 从**一枚**变成**全部**
                  —— 一家公司常同时是两三个产品的客户,而上一版只摆精判选中的那一个,
                  于是另外两个产品的页签上永远是 0(老板:「我现在看销帮AI智脑和阳光400电话还是 0 商机」)。
                  主产品实心、次产品描边、下架的灰着,见 `ProductChips`。 */}
              <ProductChips card={card} onPick={onPickProduct} />
              {/* 1009(契约 §4):评论线卡的小标签(「竞品评论区」「作者已回复」);缺席 / 空不渲染。 */}
              {(card.tags ?? [])
                .filter((t) => !!t)
                .map((t) => (
                  <span
                    key={t}
                    data-testid="opp-card-tag"
                    className="rounded-full border border-dashed px-1.5 py-px text-muted-foreground"
                  >
                    {t}
                  </span>
                ))}
              {/* ★ 一格都摆不出来 = 精判没绑上产品(契约 §13.7:越界就是空,不猜)——
                  卡上**不留占位**,这一格空着本身是给运营看的信号(线索库那张表写「—」,
                  因为表格的列必须对齐,而卡上的 pill 行不是表格)。 */}
            </p>
          </div>
        </div>
        <div className="flex shrink-0 basis-full flex-wrap items-start justify-between gap-2 sm:basis-auto sm:justify-end">
          <ScoreBlock card={card} />
          {assignSlot}
        </div>
      </header>

      <div className="space-y-3 px-4 py-3">
        {/* ── ⓪ 「这家最近」:跨源合卡的可见形态(契约 §14.2)──────────────
            合卡之前这是三张卡,销售看不出它们是同一家 —— 而"招标 + 公众号 + 知乎"
            三条线同时有动作,恰恰是这家今天就该打的理由。 */}
        {showRecent && (
          <section className="rounded-lg border border-dashed bg-muted/30 px-3 py-2">
            <h4 className="mb-1 text-2xs font-bold uppercase tracking-[0.1em] text-muted-foreground">
              这家最近
            </h4>
            <ol className="space-y-1">
              {recent.map((s, i) => (
                <li
                  key={`${s.source}-${s.happenedAt ?? i}`}
                  className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 text-xs"
                >
                  <span className="font-semibold">{s.label || sourceLabel(s.source)}</span>
                  {monthDayText(s.happenedAt) && (
                    <span className="text-2xs text-muted-foreground">
                      {monthDayText(s.happenedAt)}
                    </span>
                  )}
                  {/* 点不开的不渲染成链接 —— 与事实块同一条纪律。 */}
                  {s.sourceUrl && (
                    <a
                      href={s.sourceUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-0.5 whitespace-nowrap text-2xs font-semibold text-primary underline-offset-2 hover:underline"
                    >
                      看原文
                      <ExternalLink className="h-3 w-3" />
                    </a>
                  )}
                </li>
              ))}
            </ol>
          </section>
        )}

        {/* ── ① 事实:每条能点开原文 ──────────────────────────────────── */}
        <section>
          <h4 className="mb-1.5 text-2xs font-bold uppercase tracking-[0.1em] text-muted-foreground">
            事实 · 每条都能点开原文
          </h4>
          {shownFacts.length === 0 ? (
            <p className="text-xs text-muted-foreground">这条没有可追溯的原文，先不下结论。</p>
          ) : (
            <ul className="space-y-2">
              {shownFacts.map((f) => (
                <li key={f.evidenceId} className="text-sm leading-relaxed">
                  <span className="mr-1.5 inline-block rounded bg-foreground/5 px-1.5 py-px align-[2px] text-2xs font-semibold text-muted-foreground">
                    {f.label}
                  </span>
                  <span>{tidyMoneyDecimals(f.text)}</span>{" "}
                  <a
                    href={f.sourceUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-0.5 whitespace-nowrap text-xs font-semibold text-primary underline-offset-2 hover:underline"
                  >
                    {/* ★ 第一方线的「原文」是自家的会话页(契约 §14.1:自家页面算原文)——
                        写「看原文」会让人以为要跳到外网某处,而那一下其实是回自己的客服台。 */}
                    {firstParty ? "查看会话" : "看原文"}
                    <ExternalLink className="h-3 w-3" />
                  </a>
                </li>
              ))}
            </ul>
          )}
          {facts.length > shownFacts.length && (
            <button
              type="button"
              onClick={() => setShowAllFacts(true)}
              className="mt-1 text-xs font-semibold text-primary hover:underline"
            >
              还有 {facts.length - shownFacts.length} 条事实
            </button>
          )}
        </section>

        {/* ── ② 推断:与事实分开排版,明写「不是事实」 ────────────────── */}
        {card.inference && (
          <section className="rounded-lg border border-dashed border-warning/50 bg-warning/5 px-3 py-2.5">
            <h4 className="mb-1 flex items-center gap-1.5 text-2xs font-bold text-warning">
              <Sparkles className="h-3.5 w-3.5" />
              以下是 AI 推断，不是事实
            </h4>
            <p className="text-sm leading-relaxed text-foreground/90">
              {tidyMoneyDecimals(card.inference.text)}
            </p>
            {/* ★ 接手拿到产物之后,这块**不再渲染** —— 否则同一张卡上会并排摆两段
                几乎一样的话术(卡自带的 `inference.opener` 和 AI 现出的 `deliverable`),
                用户不知道该复制哪一段,而"该复制哪段"这个问题本身就不该存在。
                产物是后出的、带 targetUrl 的那一份,它胜出。 */}
            {card.inference.opener && !(card.taken && card.deliverable) && (
              <div className="mt-2 rounded-md border bg-background/70 px-2.5 py-2">
                <div className="mb-1 flex items-center justify-between gap-2">
                  <span className="text-2xs font-semibold text-muted-foreground">开场白</span>
                  <button
                    type="button"
                    onClick={() => void copyText(card.inference!.opener!, "开场白")}
                    className="inline-flex items-center gap-1 text-2xs font-semibold text-primary hover:underline"
                  >
                    <Copy className="h-3 w-3" />
                    复制
                  </button>
                </div>
                <p className="text-xs leading-relaxed text-muted-foreground">
                  {tidyMoneyDecimals(card.inference.opener)}
                </p>
              </div>
            )}
          </section>
        )}

        {/* ── ③ 接手产物:接手之后才有,免费 ─────────────────────────── */}
        {card.taken && card.deliverable && <DeliverablePanel deliverable={card.deliverable} />}

        {/* ── ④ 联系方式 + 主按钮(+ 收费的次按钮)────────────────────── */}
        <section className="flex flex-wrap items-center gap-x-4 gap-y-2 border-t pt-3">
          <div className="min-w-0 flex-1 basis-40">
            <div className="flex items-center gap-1.5 text-sm">
              {/* ★ 脱敏退役(契约 §12.4):没号码就说「无电话」。上一版摆一串 `138****5678`
                  暗示"花钱就能看到",而很多主体公网上根本查不到 —— 那是在卖一个
                  多半会失败的动作。 */}
              <span
                className={cn(
                  "truncate font-mono",
                  !card.contact?.phone && "text-muted-foreground"
                )}
              >
                {card.contact?.phone || "无电话"}
              </span>
            </div>
            {/* ★ 邮箱 / 官网(契约 §13.1 补漏):补全联系方式抽到的这两格批次一落了库却
                **卡面不显示** —— 一条抽到了却没人看得见的信息等于没抽,而用户还为它花了 50 积分。
                ★ 两个都做成能点的:邮箱 mailto 直接起草,官网新窗口开 —— 复制粘贴那一步本来就该省掉。 */}
            {(card.contact?.email || card.contact?.website) && (
              <p className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-2xs">
                {card.contact?.email && (
                  <a
                    href={`mailto:${card.contact.email}`}
                    className="min-w-0 truncate font-semibold text-primary underline-offset-2 hover:underline"
                  >
                    {card.contact.email}
                  </a>
                )}
                {card.contact?.website && (
                  <a
                    href={card.contact.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex min-w-0 items-center gap-0.5 truncate font-semibold text-primary underline-offset-2 hover:underline"
                  >
                    <span className="truncate">官网</span>
                    <ExternalLink className="h-3 w-3 shrink-0" />
                  </a>
                )}
              </p>
            )}
            {card.contact?.address && (
              <p className="mt-0.5 truncate text-2xs text-muted-foreground">
                {card.contact.address}
              </p>
            )}
            {/* 1015 企业库:工商画像一行(补全时企业库查到才有)。 */}
            {companyText && (
              <p
                className="mt-0.5 line-clamp-2 text-2xs text-muted-foreground"
                title={card.company?.creditCode ?? undefined}
                data-testid="acq-company-profile"
              >
                {companyText}
              </p>
            )}
            {/* ★★ 这串号码是哪儿来的(契约 §14.4)。公告上抄的、对方自己在会话里留的、
                公网搜出来的 —— 可信度差一个量级,而销售在拨号之前该知道自己手上是哪一种。
                ★ 缺 provenance 就写「来源未知」,**不回落成「公告原文」**(契约 §14.7):
                编一个出处,打错了他还以为是公告错了。 */}
            {hasReachable && (
              <p className="mt-0.5 text-2xs text-muted-foreground">
                {contactProvenanceText(card.contact?.provenance)}
              </p>
            )}
            <ContactOthers others={card.contact?.others} />
          </div>

          <div className="flex shrink-0 basis-full flex-wrap items-center gap-2 sm:basis-auto">
            {canEnrich && (
              <Button
                type="button"
                variant="outline"
                disabled={busy}
                onClick={() => void onUnlock(card.id)}
                className="min-h-11 grow sm:grow-0"
              >
                <Lock className="h-4 w-4" />
                补全联系方式 · {priceText}
              </Button>
            )}
            {!card.taken ? (
              <Button
                type="button"
                disabled={busy}
                onClick={() => void onTake(card.id)}
                className="min-h-11 grow sm:grow-0"
              >
                <HandHeart className="h-4 w-4" />
                接手（免费）
              </Button>
            ) : (
              action &&
              ActionIcon && (
                <Button
                  type="button"
                  disabled={busy}
                  onClick={runAction}
                  className="min-h-11 grow sm:grow-0"
                >
                  <ActionIcon className="h-4 w-4" />
                  {/* 同一个动作码,来源不同说的不是同一件事(`actionLabelFor` 的注释)。 */}
                  {toolOff
                    ? "复制话术,我自己联系"
                    : actionLabelFor(card.inference!.suggestedAction, card.source)}
                </Button>
              )
            )}
          </div>
        </section>

        {/* ── 五个反馈按钮 ─────────────────────────────────────────────── */}
        <FeedbackBar
          value={card.feedbackCode}
          busy={busy}
          onPick={(code) => void onFeedback(card.id, code)}
          className="border-t pt-3"
        />
      </div>
    </article>
  );
}

/** 优先级 + 四维。**不是仪表盘**:一个大数 + 一行四个小数,点不开、不画图。 */
function ScoreBlock({ card }: { card: Card }) {
  const s = card.scores;
  return (
    // ★ 窄屏排成一行基线对齐(「92 优先级 匹配95 …」),宽屏才是右上角的竖块:
    //   窄屏下它会被 flex-wrap 甩成整行,那时 text-right 让大数字孤零零飘在右边,
    //   和左边的四个小数对不上 —— 这正是"排版类改动必须真渲染看一眼"抓出来的。
    <div className="flex shrink-0 basis-full flex-wrap items-baseline gap-x-2 sm:block sm:basis-auto sm:text-right">
      <div className="text-xl font-bold leading-none text-primary">{card.priorityScore}</div>
      <div className="text-[10px] leading-none text-muted-foreground sm:mt-0.5">优先级</div>
      {s && (
        <div className="flex gap-1.5 text-[10px] leading-none text-muted-foreground sm:mt-1.5">
          <span title="与你的目标客户有多像">匹配 {s.fit}</span>
          <span title="他现在有多想买">意图 {s.intent}</span>
          <span title="这件事有多新">时机 {s.timing}</span>
          <span title="够不够得着">触达 {s.reach}</span>
        </div>
      )}
    </div>
  );
}
